import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Thumbnail,
    ThumbnailSkeleton,
    CameraThumbnail,
    GridLoadingState,
    ScrollEndFade,
} from '@/components';
import { FullSource, SourceType } from '@/types/source';
import { useInfiniteScroll } from '@/hooks';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/Tooltip';
import { EngineService, ThumbnailSize } from '@/services/engine';

const GRID_CLASS_NAME =
    'grid gap-4 grid-cols-[repeat(auto-fill,minmax(140px,1fr))]';
const SKELETON_COUNT = 9;

interface SourcesGridProps {
    sources: FullSource[];
    isSourceSelected: (id: string) => boolean;
    onToggleSource: (id: string) => void;
    onLoadMore: () => void;
    isLoading?: boolean;
    isLoadingMore: boolean;
    hasMore: boolean;
    isSourceDisabled?: (id: string) => boolean;
    disabledSourceMessage?: string;
}

export function SourcesGrid({
    sources,
    isSourceSelected,
    onToggleSource,
    onLoadMore,
    isLoading = false,
    isLoadingMore,
    hasMore,
    isSourceDisabled,
    disabledSourceMessage,
}: SourcesGridProps) {
    const { t } = useTranslation(I18nNamespace.SOURCES);
    const [container, setContainer] = useState<HTMLDivElement | null>(null);

    useInfiniteScroll(container, {
        onLoadMore,
        isLoading: isLoadingMore,
        hasMore,
    });

    const showSkeletons = isLoading && sources.length === 0;

    return (
        <div className="relative min-h-0 min-w-0 flex-1">
            <div
                ref={setContainer}
                className="h-full overflow-y-auto overflow-x-hidden"
            >
                <GridLoadingState
                    isLoading={showSkeletons}
                    isEmpty={sources.length === 0}
                    isLoadingMore={isLoadingMore}
                    isReachingEnd={!hasMore}
                    emptyMessage={t('noSourcesFound')}
                    loadingMoreMessage={t('loadingMore')}
                    endMessage={t('allSourcesLoaded')}
                    skeletonComponent={<ThumbnailSkeleton />}
                    skeletonCount={SKELETON_COUNT}
                    gridClassName={GRID_CLASS_NAME}
                >
                    {sources.map((source) => {
                        const isSelected = isSourceSelected(source.id);
                        const isDisabled =
                            isSourceDisabled?.(source.id) ?? false;
                        const isCamera = source.type === SourceType.CAMERA;
                        const title = source.displayName || source.name;
                        const card = isCamera ? (
                            <Thumbnail
                                data-testid={`sources-menu-source-${source.id}`}
                                media={
                                    <CameraThumbnail
                                        sourceId={source.id}
                                        alt={title}
                                        liveOnHover={!isDisabled}
                                        className="h-full w-full object-cover"
                                    />
                                }
                                title={title}
                                checked={isSelected}
                                disabled={isDisabled}
                                showCheckbox
                                showBorder
                                showGradients
                                onClick={() => onToggleSource(source.id)}
                            />
                        ) : (
                            <Thumbnail
                                data-testid={`sources-menu-source-${source.id}`}
                                imageSrc={EngineService.getSourceThumbnailUrl(
                                    source.id,
                                    ThumbnailSize.MQ,
                                )}
                                title={title}
                                checked={isSelected}
                                disabled={isDisabled}
                                showCheckbox
                                showBorder
                                showGradients
                                onClick={() => onToggleSource(source.id)}
                            />
                        );

                        if (!isDisabled || !disabledSourceMessage) {
                            return (
                                <div key={source.id} className="min-w-0">
                                    {card}
                                </div>
                            );
                        }

                        return (
                            <Tooltip key={source.id}>
                                <TooltipTrigger asChild>
                                    <div className="min-w-0 cursor-not-allowed">
                                        {card}
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent
                                    variant="panel"
                                    className="max-w-80"
                                >
                                    {disabledSourceMessage}
                                </TooltipContent>
                            </Tooltip>
                        );
                    })}
                </GridLoadingState>
            </div>
            <ScrollEndFade
                container={container}
                contentVersions={[
                    sources,
                    showSkeletons,
                    isLoadingMore,
                    hasMore,
                ]}
            />
        </div>
    );
}
